import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';

export const Route = createFileRoute('/account/')({ component: AccountOverview });

interface AccountProfileDto {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailConfirmed: boolean;
  roles: string[];
}

function AccountOverview() {
  const { data, isLoading } = useQuery({
    queryKey: ['account', 'profile'],
    queryFn: () => api<AccountProfileDto>('/account/profile'),
  });

  if (isLoading) return <p className="text-[14px] text-(--color-ink-muted)">Loading profile…</p>;
  if (!data) return <p className="text-[14px] text-(--color-ink-muted)">No profile data.</p>;

  return (
    <div className="space-y-6">
      <div>
        <div className="section-ref">Profile</div>
        <h2 className="mt-1.5 font-serif text-2xl font-medium tracking-tight">
          {data.name || `${data.firstName} ${data.lastName}`.trim() || data.email}
        </h2>
        <div className="mt-1 gov-rule" aria-hidden />
      </div>

      <Card>
        <CardContent className="grid gap-x-8 gap-y-5 p-7 sm:grid-cols-2">
          <Field label="Full name" value={data.name || `${data.firstName} ${data.lastName}`.trim() || '—'} />
          <Field
            label="Email"
            value={data.email}
            badge={
              data.emailConfirmed ? (
                <StatusPill tone="success" icon={<CheckCircle2 className="size-3" />}>
                  Confirmed
                </StatusPill>
              ) : (
                <StatusPill tone="warning" icon={<AlertTriangle className="size-3" />}>
                  Unconfirmed
                </StatusPill>
              )
            }
          />
          <Field label="Phone" value={data.phoneNumber || '—'} />
          <Field label="Roles" value={data.roles.length ? data.roles.join(', ') : 'Standard taxpayer'} />
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  badge,
}: {
  label: string;
  value: string;
  badge?: React.ReactNode;
}) {
  return (
    <div>
      <div className="section-ref">{label}</div>
      <div className="mt-1.5 flex items-center gap-2">
        <span className="text-[15px] font-medium text-(--color-ink)">{value}</span>
        {badge}
      </div>
    </div>
  );
}
